import { createTheme } from "@mui/material/styles";

const colorTokens = {
  primary: {
    50: "#EAF1FF",
    100: "#D1E3FF",
    200: "#B3CCFF",
    300: "#86B6FF",
    400: "#4F8FFF",
    500: "#191970",
    600: "#08357E",
    700: "#062A5A",
    800: "#041C3B",
    900: "#031422",
  },
  accent: {
    50: "#FFF9E6",
    100: "#FFF1BF",
    200: "#FFE58A",
    300: "#FFD36A",
    400: "#EBC14F",
    500: "#D4AF37",
    600: "#B8962E",
    700: "#8F6F1F",
    800: "#6B5216",
    900: "#3D2E0C",
  },
  secondary: {
    50: "#EAFDFD",
    100: "#C9F7F6",
    200: "#99ECEA",
    300: "#66DFDD",
    400: "#2FC9C6",
    500: "#0FA3A3",
    600: "#0C8A8A",
    700: "#086B6B",
    800: "#064B4B",
    900: "#033030",
  },
  neutral: {
    50: "#F9FAFB",
    100: "#F3F4F6",
    200: "#E5E7EB",
    300: "#D1D5DB",
    400: "#9CA3AF",
    500: "#6B7280",
    600: "#4B5563",
    700: "#374151",
    800: "#1F2937",
    900: "#111827",
  },
};

export const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: colorTokens.primary[500],
      dark: colorTokens.primary[700],
      light: colorTokens.primary[400],
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: colorTokens.secondary[500],
      dark: colorTokens.secondary[600],
      light: colorTokens.secondary[200],
      contrastText: "#FFFFFF",
    },
    accent: {
      main: colorTokens.accent[500],
      dark: colorTokens.accent[600],
      light: colorTokens.accent[200],
      contrastText: colorTokens.neutral[900],
    },
    background: {
      default: "#111827", /* Softer Slate/Navy */
      paper: "#1F2937",   /* Lighter Slate for cards */
    },
    text: {
      primary: "#F9FAFB",
      secondary: "#9CA3AF",
      disabled: "#4B5563",
    },
    success: {
      main: "#16A34A",
      light: "#22C55E",
      dark: "#15803D",
      contrastText: "#FFFFFF",
    },
    warning: {
      main: "#F59E0B",
      light: "#FBBF24",
      dark: "#D97706",
      contrastText: colorTokens.neutral[900],
    },
    error: {
      main: "#EF4444",
      light: "#F87171",
      dark: "#DC2626",
      contrastText: "#FFFFFF",
    },
    info: {
      main: "#0369A1",
      light: "#0EA5E9",
      dark: "#0C4A6E",
      contrastText: "#FFFFFF",
    },
    divider: colorTokens.neutral[700],
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  typography: {
    fontFamily: '"Philosopher", -apple-system, BlinkMacSystemFont, sans-serif',
    h1: {
      fontSize: "clamp(2rem, 5vw, 3.5rem)",
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
      fontWeight: 700,
      lineHeight: 1.25,
    },
    h3: {
      fontSize: "clamp(1.25rem, 3vw, 2rem)",
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: "clamp(1.125rem, 2.5vw, 1.5rem)",
      fontWeight: 600,
      lineHeight: 1.35,
    },
    h5: {
      fontSize: "clamp(1rem, 2vw, 1.25rem)",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: "clamp(0.875rem, 1.5vw, 1rem)",
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 600,
      textTransform: "none",
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          textTransform: "none",
          fontWeight: 600,
          minHeight: "44px",
          padding: "0.75rem 1.5rem",
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: colorTokens.primary[600],
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: "44px",
          minHeight: "44px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          backgroundImage: "none",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "9999px",
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            minHeight: "44px",
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colorTokens.primary[400],
          textDecorationColor: "transparent",
          transition: "color 0.2s ease, text-decoration-color 0.2s ease",
          "&:hover": {
            color: colorTokens.primary[300],
            textDecorationColor: colorTokens.primary[300],
          },
          "&:focus-visible": {
            outline: `2px solid ${colorTokens.primary[500]}`,
            outlineOffset: "2px",
          },
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          minWidth: "44px",
          minHeight: "44px",
        },
      },
    },
  },
});

export { colorTokens };
